import { addClass, getEventPath, isDefined, isObject } from '@tyler-components-web/core';
import { tylIconCheckBox, tylIconCheckBoxOutlineBlank } from '@tylertech/tyler-icons/standard';
import { ICON_CLASS_NAME } from '../constants';
import { isDeepEqual } from '../core/utils';
import { LINEAR_PROGRESS_CONSTANTS } from '../linear-progress';
import { LIST_CONSTANTS } from '../list/list';
import { LIST_ITEM_CONSTANTS } from '../list/list-item';
import { PopupAnimationType, PopupPlacement, POPUP_CONSTANTS } from '../popup';
import { PROGRESS_SPINNER_CONSTANTS } from '../progress-spinner';
import { SKELETON_CONSTANTS } from '../skeleton';
import { ListDropdownAsyncStyle, ListDropdownType, LIST_DROPDOWN_CONSTANTS } from './list-dropdown-constants';
export var ListDropdownOptionType;
(function (ListDropdownOptionType) {
    ListDropdownOptionType[ListDropdownOptionType["Option"] = 0] = "Option";
    ListDropdownOptionType[ListDropdownOptionType["Group"] = 1] = "Group";
})(ListDropdownOptionType || (ListDropdownOptionType = {}));
/**
 * Creates the floating dropdown element container.
 * @param config
 * @param targetElement
 */
export function createDropdown(config, targetElement, isMobile) {
    var dropdownElement = isMobile ? createDialogDropdown() : createPopupDropdown(config, targetElement);
    var dropdownId = "list-dropdown-popup-" + config.id;
    // Prevent mousedown events from changing focus. We keep focus in the target element at all times while open.
    dropdownElement.addEventListener('mousedown', function (evt) {
        var composedPath = getEventPath(evt);
        var isWithinDropdownElement = composedPath.find(function (el) { return el.id === dropdownId || el.id === config.id; });
        if (isWithinDropdownElement) {
            evt.preventDefault();
        }
    });
    // Set roles and other attributes based on our type
    switch (config.type) {
        case ListDropdownType.Menu:
            dropdownElement.setAttribute('role', 'menu');
            dropdownElement.setAttribute('aria-orientation', 'vertical');
            break;
        default:
            dropdownElement.setAttribute('role', 'listbox');
    }
    if (config.id) {
        dropdownElement.id = dropdownId;
    }
    if (config.popupClasses) {
        addClass(config.popupClasses, dropdownElement);
    }
    return dropdownElement;
}
export function createPopupDropdown(config, targetElement) {
    var popupElement = document.createElement(POPUP_CONSTANTS.elementName);
    popupElement.targetElement = targetElement;
    popupElement.placement = config.popupPlacement || PopupPlacement.BottomLeft;
    popupElement.manageFocus = false;
    popupElement.static = !!config.popupStatic;
    if (config.popupPositionAdjustment) {
        popupElement.positionAdjustment = config.popupPositionAdjustment;
    }
    // Set the animations based on our type
    switch (config.type) {
        case ListDropdownType.Menu:
            popupElement.animationType = PopupAnimationType.Menu;
            break;
        case ListDropdownType.None:
            popupElement.animationType = PopupAnimationType.None;
            break;
        default:
            popupElement.animationType = PopupAnimationType.Dropdown;
    }
    return popupElement;
}
export function createDialogDropdown() {
    var dialogElement = document.createElement('tcw-dialog');
    dialogElement.fullscreen = true;
    return dialogElement;
}
export function buildDialogContent(config, childElement, closeCb) {
    var scaffold = document.createElement('tcw-scaffold');
    scaffold.style.overflow = 'auto';
    var container = document.createElement('div');
    container.slot = 'body';
    container.appendChild(childElement);
    scaffold.appendChild(container);
    var headerContainer = document.createElement('tcw-toolbar');
    headerContainer.slot = 'header';
    scaffold.appendChild(headerContainer);
    var header = document.createElement('h1');
    header.slot = 'start';
    header.style.fontSize = '1.25rem';
    header.style.lineHeight = '2rem';
    header.style.fontWeight = '400';
    header.style.letterSpacing = '0.0125em';
    header.classList.add('tyl-typography--title');
    header.textContent = config.dialogTitle || 'Choose option';
    headerContainer.appendChild(header);
    var closeIconButton = document.createElement('tcw-icon-button');
    closeIconButton.slot = 'end';
    headerContainer.appendChild(closeIconButton);
    var button = document.createElement('button');
    button.id = "list-dropdown-dialog-close-" + config.id;
    button.type = 'button';
    button.setAttribute('aria-label', 'Close the dropdown');
    button.addEventListener('click', function () {
        if (typeof closeCb === 'function') {
            closeCb();
        }
    });
    closeIconButton.appendChild(button);
    var iconElement = document.createElement('tcw-icon');
    iconElement.name = 'close';
    button.appendChild(iconElement);
    return scaffold;
}
export function createList(config) {
    var listElement = document.createElement(LIST_CONSTANTS.elementName);
    listElement.id = "list-dropdown-list-" + config.id;
    listElement.propagateClick = false;
    return listElement;
}
/**
 * Creates the list to place inside of the dropdown.
 * @param config
 */
export function createListItems(config, listElement, options) {
    var groups = getOptionsByGroup(options || config.options); // Ensure the options are provided in the form a group (if no groups provided, then we have one anonymous group of options)
    var limitOptions = config.optionLimit ? true : false;
    var optionLimit = config.optionLimit || 0;
    var optionIndex = 0;
    // Iterate over our groups and render the optional headers and options for that group
    for (var _i = 0, groups_1 = groups; _i < groups_1.length; _i++) {
        var group = groups_1[_i];
        var optionParent = listElement;
        // Check for a group header builder callback
        if (group.builder && typeof group.builder === 'function') {
            var headerElement = group.builder(group);
            if (headerElement) {
                var groupWrapper = document.createElement('div');
                groupWrapper.classList.add(LIST_DROPDOWN_CONSTANTS.classes.GROUP_WRAPPER);
                optionParent = groupWrapper;
                groupWrapper.appendChild(headerElement);
                listElement.appendChild(groupWrapper);
            }
        }
        else if (group.text) {
            // We are rendering just text as a group header so create the group header element with predefined styles
            var groupWrapper = document.createElement('div');
            groupWrapper.classList.add(LIST_DROPDOWN_CONSTANTS.classes.GROUP_WRAPPER);
            var listHeaderElement = document.createElement('div');
            listHeaderElement.textContent = group.text;
            listHeaderElement.classList.add('tyl-typography--overline');
            listHeaderElement.style.fontFamily = 'Roboto,sans-serif';
            listHeaderElement.style.fontSize = '0.75rem';
            listHeaderElement.style.lineHeight = '2rem';
            listHeaderElement.style.letterSpacing = '0.16667em';
            listHeaderElement.style.fontWeight = '500';
            listHeaderElement.style.margin = '4px 8px';
            optionParent = groupWrapper;
            groupWrapper.appendChild(listHeaderElement);
            listElement.appendChild(groupWrapper);
        }
        var _loop_1 = function (option) {
            // First we check if this option is just a divider
            if (option.divider) {
                var dividerElement = createDivider();
                listElement.appendChild(dividerElement);
                return "continue";
            }
            // Check if an option limit has been provided to reduce amount of options that need to be rendered
            if (limitOptions && --optionLimit < 0) {
                return "break";
            }
            optionIndex++;
            // Create and configure the list element
            var isSelected = config.selectedValues ? config.selectedValues.some(function (v) { return isDeepEqual(v, option.value); }) : false;
            var listItemElement = document.createElement(LIST_ITEM_CONSTANTS.elementName);
            listItemElement.value = option.value;
            listItemElement.id = "list-dropdown-option-" + config.id + "-" + optionIndex;
            listItemElement.style.cursor = 'pointer';
            // Add any CSS classes to the option list-item
            if (option.optionClass) {
                addClass(option.optionClass, listItemElement);
            }
            // Set role based on type
            switch (config.type) {
                case ListDropdownType.Menu:
                    listItemElement.setAttribute('role', 'menuitem');
                    break;
                case ListDropdownType.None:
                    break;
                default:
                    listItemElement.setAttribute('role', 'option');
            }
            if (config.dense) {
                listItemElement.dense = true;
            }
            // Check for a custom option template builder
            if (config.optionBuilder && typeof config.optionBuilder === 'function') {
                var element = config.optionBuilder(option, listItemElement);
                if (element) {
                    if (typeof element === 'string') {
                        listItemElement.innerHTML = element;
                    }
                    else {
                        listItemElement.appendChild(element);
                    }
                }
            }
            else {
                if (typeof config.transform !== 'function') {
                    listItemElement.textContent = option.label || '';
                }
                else {
                    var result = config.transform(option.label);
                    if (typeof result === 'string') {
                        listItemElement.textContent = result;
                    }
                    else if (typeof result === 'object' && result.nodeType !== undefined) {
                        listItemElement.appendChild(result);
                    }
                }
            }
            // Leading element/icon
            if (option.leadingBuilder) {
                var element = option.leadingBuilder();
                if (isObject(element)) {
                    element.slot = 'leading';
                    listItemElement.appendChild(element);
                }
            }
            else if (option.leadingIcon) {
                var leadingIconElement = createIconElement(option.leadingIconType, option.leadingIcon, option.leadingIconClass || config.iconClass);
                leadingIconElement.slot = 'leading';
                listItemElement.appendChild(leadingIconElement);
            }
            // Trailing element/icon
            if (option.trailingBuilder) {
                var element = option.trailingBuilder();
                if (isObject(element)) {
                    element.slot = 'trailing';
                    listItemElement.appendChild(element);
                }
            }
            else if (option.trailingIcon) {
                var trailingIconElement = createIconElement(option.trailingIconType, option.trailingIcon, option.trailingIconClass || config.iconClass);
                trailingIconElement.slot = 'trailing';
                listItemElement.appendChild(trailingIconElement);
            }
            // Update the disabled state
            if (option.disabled) {
                listItemElement.disabled = option.disabled;
                listItemElement.setAttribute('aria-disabled', 'true');
            }
            else {
                listItemElement.style.cursor = 'pointer';
                listItemElement.setAttribute('aria-disabled', 'false');
            }
            // Update the selected state
            if (isSelected) {
                listItemElement.selected = true;
            }
            listItemElement.setAttribute('aria-selected', isSelected ? 'true' : 'false');
            // If multiple selections are enabled then we need to create and append a leading checkbox element
            if (config.multiple) {
                var checkboxElement = createCheckboxElement(isSelected);
                listItemElement.appendChild(checkboxElement);
                listItemElement.setAttribute('aria-selected', "" + isSelected);
                listItemElement.setAttribute('aria-checked', "" + isSelected);
            }
            if (option.elementAttributes) {
                option.elementAttributes.forEach(function (value, key) {
                    listItemElement.setAttribute(key, value);
                });
            }
            // If we have any child options, we need to render a child menu for this list item
            if (!option.disabled && typeof config.cascadingElementFactory === 'function' && Array.isArray(option.options) && option.options.length) {
                // Create the trailing indicator icon to show that a child menu exists for this option. 
                var optionIconElement = document.createElement('tcw-icon');
                optionIconElement.name = 'arrow_right';
                optionIconElement.slot = 'trailing';
                listItemElement.appendChild(optionIconElement);
                var nonDividerOptions = group.options.filter(function (o) { return !o.divider; });
                // Create the nested cascading element wrapper
                var factoryConfig = {
                    index: nonDividerOptions.indexOf(option),
                    options: option.options,
                    parentValue: option.value
                };
                var cascadingElement = config.cascadingElementFactory.call(null, factoryConfig);
                cascadingElement.appendChild(listItemElement);
                listItemElement = cascadingElement;
            }
            optionParent.appendChild(listItemElement);
        };
        // Loop over the options for this group
        for (var _a = 0, _b = group.options; _a < _b.length; _a++) {
            var option = _b[_a];
            var state_1 = _loop_1(option);
            if (state_1 === "break")
                break;
        }
    }
}
export function createCheckboxElement(selected) {
    var checkboxElement = document.createElement('tcw-icon');
    checkboxElement.setAttribute(LIST_DROPDOWN_CONSTANTS.attributes.CHECKBOX_ELEMENT, '');
    checkboxElement.name = selected ? tylIconCheckBox.name : tylIconCheckBoxOutlineBlank.name;
    checkboxElement.slot = 'leading';
    checkboxElement.style.marginRight = '16px';
    checkboxElement.style.verticalAlign = 'middle';
    return checkboxElement;
}
function createDivider() {
    var divider = document.createElement('tcw-divider');
    divider.setAttribute('aria-hidden', 'true');
    return divider;
}
function createIconElement(type, iconName, iconClass) {
    if (type === void 0) { type = 'font'; }
    if (type === 'component') {
        var icon = document.createElement('tcw-icon');
        if (iconClass) {
            icon.classList.add(iconClass);
        }
        icon.setAttribute('aria-hidden', 'true');
        icon.name = iconName;
        return icon;
    }
    var iconElement = document.createElement('i');
    iconElement.classList.add(iconClass || ICON_CLASS_NAME);
    iconElement.setAttribute('aria-hidden', 'true');
    iconElement.textContent = iconName;
    return iconElement;
}
export function createAsyncElement(asyncStyle) {
    switch (asyncStyle) {
        case ListDropdownAsyncStyle.Skeleton:
            return createSkeletonElement();
        default:
            return createSpinnerElement();
    }
}
function createSpinnerElement() {
    var div = document.createElement('div');
    div.style.display = 'flex';
    div.style.justifyContent = 'center';
    div.style.alignItems = 'center';
    div.style.padding = '8px';
    div.style.boxSizing = 'border-box';
    var spinner = document.createElement(PROGRESS_SPINNER_CONSTANTS.elementName);
    spinner.diameter = 24;
    spinner.strokeWidth = 8;
    spinner.style.height = '24px';
    spinner.style.width = '24px';
    div.appendChild(spinner);
    return div;
}
function createSkeletonElement() {
    var list = document.createElement('tcw-list');
    var div = document.createElement('div');
    div.style.minWidth = '192px';
    for (var i = 0; i < 3; i++) {
        var listItemSkeleton = document.createElement(SKELETON_CONSTANTS.elementName);
        listItemSkeleton.setAttribute('list-item', '');
        div.appendChild(listItemSkeleton);
    }
    list.appendChild(div);
    return list;
}
export function createBusyElement() {
    var linearProgress = document.createElement(LINEAR_PROGRESS_CONSTANTS.elementName);
    linearProgress.determinate = false;
    linearProgress.style.position = 'absolute';
    linearProgress.style.top = '0';
    linearProgress.style.left = '0';
    linearProgress.style.right = '0';
    linearProgress.style.width = '100%';
    return linearProgress;
}
function getOptionsByGroup(options) {
    return isListDropdownOptionType(options, ListDropdownOptionType.Group) ? options : [{ text: '', options: options }];
}
/**
 * Determines if the provided options are of the specified type.
 * @param options The options either grouped or individual.
 * @param type The type of option to detect.
 */
export function isListDropdownOptionType(options, type) {
    var isOptionGroups = options.some(function (o) { return isDefined(o) && isObject(o) && o.hasOwnProperty('options') && (o.hasOwnProperty('text') || o.hasOwnProperty('builder')); });
    var isOptionTypes = options.some(function (o) { return isDefined(o) && isObject(o) && o.hasOwnProperty('label') && (o.hasOwnProperty('value')); });
    return (isOptionGroups && type === ListDropdownOptionType.Group) || (isOptionTypes && type === ListDropdownOptionType.Option);
}
